import { Controller } from "@hotwired/stimulus"

// Microsoft's access tokens last at least an hour; a token is reused for half that, so it's never handed over expired.
const TOKEN_REUSE_MS = 30 * 60 * 1000

// Microsoft's OneDrive and SharePoint file picker (v8), opened in a popup on the connected account's SharePoint. The
// picker talks back over a MessagePort: it asks for an access token per resource (answered from the token endpoint,
// which mints one for that resource) and sends the picked items.
//
// Two modes:
// - "items": pick any number of folders and files; each becomes a row cloned from the template into the list, with
//   its drive and item ids in hidden inputs (the server looks up its name, path and kind on save).
// - "workbook": pick one spreadsheet for a workbook form, filling its drive id, item id and name inputs.
export default class extends Controller {
  static targets = ["template", "list", "empty", "driveIdInput", "itemIdInput", "nameInput", "nameDisplay"]
  static values = {
    baseUrl: String,
    tokenUrl: String,
    mode: { type: String, default: "items" },
  }

  disconnect() {
    this.cleanUp()
  }

  async open(event) {
    event.preventDefault()
    if (!this.baseUrlValue) {
      alert("Reconnect Microsoft 365 to choose files: we couldn't find this account's SharePoint.")
      return
    }

    this.cleanUp()
    this.channelId = crypto.randomUUID()
    this.popup = window.open("", "MicrosoftPicker", "width=1080,height=680")
    this.onWindowMessage = this.handleWindowMessage.bind(this)
    window.addEventListener("message", this.onWindowMessage)

    const token = await this.fetchToken(this.baseUrlValue)
    if (!token) {
      this.popup.close()
      alert("Microsoft 365 didn't allow access. Reauthorize the integration and try again.")
      return
    }
    this.submitPickerForm(token)
  }

  // The base URL is the account's OneDrive host (contoso-my.sharepoint.com), or its organization's SharePoint for an
  // account without a OneDrive licence; the picker then opens in that site's document library ("Shared Documents" is
  // its address on every site, whatever its title) and leaves out "My files".
  hasOneDrive() {
    return new URL(this.baseUrlValue).hostname.endsWith("-my.sharepoint.com")
  }

  pickerOptions() {
    const workbook = this.modeValue === "workbook"
    const oneDrive = this.hasOneDrive()
    return {
      sdk: "8.0",
      entry: oneDrive
        ? { oneDrive: { files: {} } }
        : { sharePoint: { byPath: { web: this.baseUrlValue, list: "Shared Documents", fallbackToRoot: true } } },
      authentication: {},
      messaging: { origin: window.location.origin, channelId: this.channelId },
      typesAndSources: {
        mode: workbook ? "files" : "all",
        ...(workbook ? { filters: [".xlsx", ".csv"] } : {}),
        pivots: { oneDrive, recent: true, shared: true, sharedLibraries: true, myOrganization: true },
      },
      selection: { mode: workbook ? "single" : "multiple" },
    }
  }

  submitPickerForm(token) {
    const query = new URLSearchParams({ filePicker: JSON.stringify(this.pickerOptions()), locale: "en-us" })
    const doc = this.popup.document
    const form = doc.createElement("form")
    form.setAttribute("action", `${this.baseUrlValue}/_layouts/15/FilePicker.aspx?${query}`)
    form.setAttribute("method", "POST")

    const tokenInput = doc.createElement("input")
    tokenInput.setAttribute("type", "hidden")
    tokenInput.setAttribute("name", "access_token")
    tokenInput.setAttribute("value", token)
    form.appendChild(tokenInput)

    doc.body.append(form)
    form.submit()
  }

  handleWindowMessage(event) {
    if (event.source !== this.popup) return

    const message = event.data
    if (message.type === "initialize" && message.channelId === this.channelId) {
      this.port = event.ports[0]
      this.port.addEventListener("message", (portEvent) => this.handlePortMessage(portEvent.data))
      this.port.start()
      this.port.postMessage({ type: "activate" })
    }
  }

  async handlePortMessage(payload) {
    if (payload.type !== "command") return

    this.port.postMessage({ type: "acknowledge", id: payload.id })
    const command = payload.data

    switch (command.command) {
      case "authenticate": {
        const token = await this.fetchToken(command.resource)
        this.respond(payload.id, token
          ? { result: "token", token }
          : { result: "error", error: { code: "unableToObtainToken", message: "No token" } })
        break
      }
      case "pick":
        this.pick(command.items || [])
        this.respond(payload.id, { result: "success" })
        this.cleanUp()
        break
      case "close":
        this.cleanUp()
        break
      default:
        this.respond(payload.id, { result: "error", error: { code: "unsupportedCommand", message: command.command } })
    }
  }

  respond(id, data) {
    this.port.postMessage({ type: "result", id, data })
  }

  pick(items) {
    if (this.modeValue === "workbook") {
      const item = items[0]
      if (item) this.fillWorkbook(item)
    } else {
      items.forEach((item) => this.addRow(item))
    }
  }

  fillWorkbook(item) {
    this.setInput(this.driveIdInputTarget, item.parentReference.driveId)
    this.setInput(this.itemIdInputTarget, item.id)
    // The picker doesn't promise a name; the server looks it up on save when it's missing.
    this.setInput(this.nameInputTarget, item.name ?? "")
    if (this.hasNameDisplayTarget) this.nameDisplayTarget.value = item.name ?? ""
  }

  addRow(item) {
    const driveId = item.parentReference.driveId
    const rows = [...this.listTarget.querySelectorAll("[data-microsoft-picker-row]")]
    if (rows.some((row) => row.dataset.driveId === driveId && row.dataset.itemId === item.id)) return

    const row = this.templateTarget.content.firstElementChild.cloneNode(true)
    row.dataset.driveId = driveId
    row.dataset.itemId = item.id
    row.querySelector("[data-field='drive_id']").value = driveId
    row.querySelector("[data-field='item_id']").value = item.id
    row.querySelector("[data-field='name_input']").value = item.name ?? ""
    row.querySelector("[data-field='name']").textContent = item.name ?? ""
    this.listTarget.append(row)
    if (this.hasEmptyTarget) this.emptyTarget.remove()
  }

  remove(event) {
    event.preventDefault()
    event.target.closest("[data-microsoft-picker-row]").remove()
  }

  setInput(input, value) {
    input.value = value
    input.dispatchEvent(new Event("change", { bubbles: true }))
  }

  async fetchToken(resource) {
    this.tokens ??= new Map()
    const cached = this.tokens.get(resource)
    if (cached && Date.now() - cached.fetchedAt < TOKEN_REUSE_MS) return cached.token

    const url = `${this.tokenUrlValue}?${new URLSearchParams({ resource })}`
    const response = await fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
    if (!response.ok) return null

    const { token } = await response.json()
    this.tokens.set(resource, { token, fetchedAt: Date.now() })
    return token
  }

  cleanUp() {
    if (this.onWindowMessage) window.removeEventListener("message", this.onWindowMessage)
    this.onWindowMessage = null
    this.port?.close()
    this.port = null
    if (this.popup && !this.popup.closed) this.popup.close()
    this.popup = null
    this.tokens = null
  }
}
